import React from 'react';
import {
  ChipField,
  Loading,
  RecordContextProvider,
  SingleFieldList,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { Grid } from '@mui/material';

import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import ChipStringField from '../../customFields/ChipStringField';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import ArrayDiffField from '../utils/auditTracks/ArrayDiffField';
import DateDiffField from '../utils/auditTracks/DateDiffField';
import SelectDiffField from '../utils/auditTracks/SelectDiffField';
import StatusDiffField from '../utils/auditTracks/StatusDiffField';
import ReferenceArrayDiffField from '../utils/auditTracks/ReferenceArrayDiffField';
import ReferenceEntityField from '../../customFields/ReferenceEntityField';
import EntityDisplayNameField from '../../customFields/EntityDisplayNameField';
import countries from '../../constants/countries';
import resourceNames from '../../constants/resourceNames';
import { IBusiness } from '../../utilities/schemas/business';

const BusinessAuditDetails = () => {
  const record = useRecordContext<AuditTrack<IBusiness>>();
  const { data, isLoading } = useGetOne<AuditTrack<IBusiness>>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );
  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<IBusiness>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    id: resourceData.id?.hasChanged,
    customIds: resourceData.customIds?.hasChanged,
    name: resourceData.name?.hasChanged,
    incorporationLegalForm: resourceData.incorporationLegalForm?.hasChanged,
    residence: resourceData.residence?.hasChanged,
    registrationNumber: resourceData.registrationNumber?.hasChanged,
    swedishOrganizationNumber: resourceData.swedishOrganizationNumber?.hasChanged,
    riskScore: resourceData.riskScore?.hasChanged,
    scoreLatestCalculationDate: resourceData.scoreLatestCalculationDate?.hasChanged,
    kycOnboarding: resourceData.kycOnboarding?.hasChanged,
    kycApprovedAt: resourceData.kycApprovedAt?.hasChanged,
    status: resourceData.status?.hasChanged,
    createdAt: resourceData.createdAt?.hasChanged,
    currencies: resourceData.currencies?.hasChanged,
    conductors: resourceData.conductors?.hasChanged,
    groups: resourceData.groups?.hasChanged,
    email: resourceData.email?.hasChanged,
    phoneNumber: resourceData.phoneNumber?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.id && <TextDiffField source="id" label="ID" />}

          {hasFieldChanges.customIds && (
            <ArrayDiffField source="customIds" label="Custom IDs">
              <SingleFieldList linkType={false}>
                <ChipStringField />
              </SingleFieldList>
            </ArrayDiffField>
          )}

          {hasFieldChanges.name && <TextDiffField source="name" label="Name" />}

          {hasFieldChanges.incorporationLegalForm && (
            <TextDiffField source="incorporationLegalForm" label="Incorporation legal form" />
          )}

          {hasFieldChanges.residence && (
            <SelectDiffField source="residence" label="Residence" choices={countries} />
          )}

          {hasFieldChanges.registrationNumber && (
            <TextDiffField source="registrationNumber" label="Registration number" />
          )}

          {hasFieldChanges.swedishOrganizationNumber && (
            <TextDiffField source="swedishOrganizationNumber" label="Swedish organization number" />
          )}

          {hasFieldChanges.riskScore && <TextDiffField source="riskScore" label="Risk score" />}

          {hasFieldChanges.scoreLatestCalculationDate && (
            <DateDiffField source="scoreLatestCalculationDate" label="Score latest calculation date" />
          )}

          {hasFieldChanges.kycOnboarding && (
            <DateDiffField source="kycOnboarding" label="KYC onboarding" showTime />
          )}

          {hasFieldChanges.kycApprovedAt && (
            <DateDiffField source="kycApprovedAt" label="KYC approved at" />
          )}

          {hasFieldChanges.status && <StatusDiffField source="status" label="Status" />}

          {hasFieldChanges.createdAt && <DateDiffField source="createdAt" label="Created at" showTime />}

          {hasFieldChanges.currencies && (
            <ArrayDiffField source="currencies" label="Currencies">
              <SingleFieldList linkType={false}>
                <ChipStringField />
              </SingleFieldList>
            </ArrayDiffField>
          )}

          {hasFieldChanges.conductors && (
            <ArrayDiffField source="conductors" label="Conductors">
              <SingleFieldList linkType={false}>
                <ReferenceEntityField source="" typeKey="conductorType">
                  <EntityDisplayNameField chip />
                </ReferenceEntityField>
              </SingleFieldList>
            </ArrayDiffField>
          )}

          {hasFieldChanges.groups && (
            <ReferenceArrayDiffField
              source="groups"
              label="Groups"
              reference={resourceNames.GROUPS}
            >
              <SingleFieldList linkType={false}>
                <ChipField source="name" size="small" />
              </SingleFieldList>
            </ReferenceArrayDiffField>
          )}

          {hasFieldChanges.email && <TextDiffField source="email" label="Email" />}

          {hasFieldChanges.phoneNumber && (
            <TextDiffField source="phoneNumber" label="Phone number" />
          )}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default BusinessAuditDetails;
